/**
 * Google Analytics 4 (gtag.js) loader shared by panel and admin.
 *
 * The script is injected on first call, never from index.html, so the panel can
 * keep it unloaded until the visitor accepts analytics cookies. On the panel, page
 * views come from the stream's Enhanced Measurement ("page changes based on
 * browser history events"), which follows TanStack Router navigations. Admin
 * sends its own query-free page views instead (see `trackGtagPageView`).
 */

type Gtag = (...args: unknown[]) => void;

declare global {
    interface Window {
        dataLayer?: unknown[];
        gtag?: Gtag;
    }
}

// Analytics only. Ads storage and signals stay off: the consent dialog never asks for them.
const GRANTED = {
    analytics_storage: 'granted',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
} as const;

const DENIED = {
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
} as const;

export function gtagScriptSrc(measurementId: string) {
    return `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
}

function gtag(): Gtag {
    if (!window.gtag) {
        window.dataLayer = window.dataLayer ?? [];
        window.gtag = function gtag() {
            // gtag.js reads the `arguments` object itself, not an array.
            // biome-ignore lint/complexity/noArguments: gtag.js contract
            window.dataLayer?.push(arguments);
        };
    }
    return window.gtag;
}

function setKillSwitch(measurementId: string, disabled: boolean) {
    (window as unknown as Record<string, unknown>)[`ga-disable-${measurementId}`] = disabled;
}

/**
 * Load gtag.js once and start collection for `measurementId`. Called again after
 * `disableGtag` (re-accept without a reload), it re-grants consent and records a
 * page view for the page the visitor is on, since the first one was already sent.
 */
export function loadGtag(measurementId: string, config: Record<string, unknown> = {}) {
    if (typeof window === 'undefined') return;
    const g = gtag();
    setKillSwitch(measurementId, false);

    const src = gtagScriptSrc(measurementId);
    if (document.querySelector(`script[src="${src}"]`)) {
        g('consent', 'update', GRANTED);
        g('event', 'page_view', { send_to: measurementId });
        return;
    }

    g('consent', 'default', GRANTED);
    g('js', new Date());
    g('config', measurementId, {
        allow_google_signals: false,
        allow_ad_personalization_signals: false,
        ...config,
    });
    const script = document.createElement('script');
    script.async = true;
    script.src = src;
    document.head.appendChild(script);
}

/** Stop collection for `measurementId`. A loaded script can't be unloaded, so this disables it. */
export function disableGtag(measurementId: string) {
    if (typeof window === 'undefined') return;
    setKillSwitch(measurementId, true);
    window.gtag?.('consent', 'update', DENIED);
}

/** The page URL without its query string or hash, which can carry emails and usernames. */
export function pageLocationWithoutQuery(location: Pick<Location, 'origin' | 'pathname'> = window.location) {
    return `${location.origin}${location.pathname}`;
}

/**
 * Pin `page_location` to the query-free URL for every later hit. Pinning it with
 * `set` also covers hits gtag sends on its own (Enhanced Measurement), so a raw
 * `?q=` never reaches Google.
 */
export function pinGtagPageLocation() {
    if (typeof window === 'undefined' || !window.gtag) return null;
    const page_location = pageLocationWithoutQuery();
    window.gtag('set', { page_location });
    return page_location;
}

/** Re-pin the query-free URL after a navigation and send a page view for it. */
export function trackGtagPageView(measurementId: string) {
    const page_location = pinGtagPageLocation();
    if (!page_location) return;
    window.gtag?.('event', 'page_view', { send_to: measurementId, page_location, page_title: document.title });
}
